import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { operatorsSkillMutationOptions } from "../../lib/operatorsSkillsReactQuery";
import type { OperatorsSkillDetail } from "../../operators";
import { OPERATORS_SKILL_PATH } from "../../operatorsRoutes";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { SKILL_NAME_PATTERN, buildSkillMarkdown } from "./skillMarkdown";

function DialogError({ children }: { children: string | null }) {
  return children ? (
    <p role="alert" className="text-sm text-destructive">
      {children}
    </p>
  ) : null;
}

export function CreateSkillDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation(operatorsSkillMutationOptions(queryClient));
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const skill = await mutation.mutateAsync({
        files: { "SKILL.md": buildSkillMarkdown({ description, instructions, name }) },
        kind: "create",
      });
      onClose();
      await navigate({ params: { skillId: skill.id }, to: OPERATORS_SKILL_PATH });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create skill.");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogPopup className="max-h-[calc(100dvh-2rem)] max-w-2xl overflow-y-auto">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>New skill</DialogTitle>
            <DialogDescription>
              Codex reads the description to decide when to use the skill. Add scripts or references
              after creating it.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="skill-name">Name</Label>
              <Input
                id="skill-name"
                autoFocus
                required
                pattern={SKILL_NAME_PATTERN.source}
                maxLength={64}
                value={name}
                onChange={(event) => setName(event.target.value.toLowerCase())}
                placeholder="weekly-report"
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, digits, and hyphens. This can't be changed later.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="skill-description">When to use it</Label>
              <Textarea
                id="skill-description"
                required
                rows={2}
                maxLength={1024}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Summarize last week's merged PRs. Use when asked for a weekly engineering report."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="skill-instructions">Instructions</Label>
              <Textarea
                id="skill-instructions"
                rows={8}
                value={instructions}
                onChange={(event) => setInstructions(event.target.value)}
                placeholder="Step-by-step guidance in Markdown."
              />
            </div>
            <DialogError>{error}</DialogError>
          </DialogPanel>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Creating…" : "Create skill"}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}

export function ImportSkillDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation(operatorsSkillMutationOptions(queryClient));
  const [zip, setZip] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!zip) return;
    setError(null);
    try {
      const skill = await mutation.mutateAsync({ kind: "import", zip });
      onClose();
      await navigate({ params: { skillId: skill.id }, to: OPERATORS_SKILL_PATH });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not import skill.");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogPopup className="max-w-lg">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Import skill</DialogTitle>
            <DialogDescription>
              Upload a .zip with SKILL.md at its root or inside one folder. Scripts, references, and
              assets are preserved up to 5 MiB.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-3">
            <Label htmlFor="skill-package">Skill package</Label>
            <Input
              id="skill-package"
              type="file"
              accept=".zip,application/zip"
              required
              onChange={(event) => setZip(event.target.files?.[0] ?? null)}
            />
            <DialogError>{error}</DialogError>
          </DialogPanel>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!zip || mutation.isPending}>
              {mutation.isPending ? "Importing…" : "Import skill"}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}

export function DuplicateSkillDialog({
  onClose,
  skill,
}: {
  onClose: () => void;
  skill: OperatorsSkillDetail;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation(operatorsSkillMutationOptions(queryClient));
  const [name, setName] = useState(`${skill.name}-copy`.slice(0, 64));
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const copy = await mutation.mutateAsync({ id: skill.id, kind: "duplicate", name });
      onClose();
      await navigate({ params: { skillId: copy.id }, to: OPERATORS_SKILL_PATH });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not duplicate skill.");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogPopup className="max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Duplicate {skill.name}</DialogTitle>
            <DialogDescription>
              Create an editable copy that you own. Like every active skill, it reaches everyone's
              new threads.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-2">
            <Label htmlFor="duplicate-skill-name">New name</Label>
            <Input
              id="duplicate-skill-name"
              autoFocus
              required
              pattern={SKILL_NAME_PATTERN.source}
              maxLength={64}
              value={name}
              onChange={(event) => setName(event.target.value.toLowerCase())}
            />
            <DialogError>{error}</DialogError>
          </DialogPanel>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Duplicating…" : "Duplicate skill"}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}

export function AddSkillFileDialog({
  existingPaths,
  onAdd,
  onClose,
}: {
  existingPaths: readonly string[];
  onAdd: (path: string) => void;
  onClose: () => void;
}) {
  const [path, setPath] = useState("");
  const normalizedPath = path.trim();
  const duplicate = existingPaths.includes(normalizedPath);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!normalizedPath || duplicate) return;
    onAdd(normalizedPath);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogPopup className="max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add a skill file</DialogTitle>
            <DialogDescription>
              Use a relative path inside the skill package, such as scripts/run.sh or
              references/api.md.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-2">
            <Label htmlFor="skill-file-path">File path</Label>
            <Input
              id="skill-file-path"
              autoFocus
              value={path}
              onChange={(event) => setPath(event.target.value)}
              placeholder="references/api.md"
              aria-invalid={duplicate || undefined}
              aria-describedby={duplicate ? "skill-file-path-error" : undefined}
            />
            {duplicate ? (
              <p id="skill-file-path-error" role="alert" className="text-sm text-destructive">
                This file already exists. Select it from the file list to edit it.
              </p>
            ) : null}
          </DialogPanel>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!normalizedPath || duplicate}>
              Add file
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
